/**
 * Describe Your Trip AI search only — recommendation chips and submit resolution.
 * Standard hotel typeahead / autocomplete uses the legacy hotels path elsewhere.
 */
import CONFIG from 'isomorphic-config'
import fetch from 'isomorphic-unfetch'

import type { LOCATION_SEARCH_TYPE } from '@/types'

import { logAiSearchRequestFailed } from './logAiSearch'

type FetchAiQueryParams = {
  query: string
  signal?: AbortSignal
}

function isAbortError(error: unknown) {
  return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
}

export function buildAiSearchUrl(query: string): string {
  const params = new URLSearchParams({
    query,
    searchModes: 'AI_SEARCH,VECTOR_SEARCH',
    'limit[AI_SEARCH]': '1',
    'limit[VECTOR_SEARCH]': '4',
  })

  return `${CONFIG.client.typeahead.hotelsAiSearch}?${params.toString()}`
}

/**
 * `itemDisplayName` disambiguates with state/country (e.g. "Florida, NY" vs. the bare
 * `itemName` "Florida", a small town easily mistaken for the state) and is identical to
 * `itemName` for `AI_QUERY` matches, so preferring it is safe across all result types.
 */
export function getAiSuggestionLabel(item: LOCATION_SEARCH_TYPE) {
  return item.itemDisplayName || item.itemName || item.displayLine1 || item.displayName || ''
}

/**
 * `hotels/aisearch` can return `CITY` matches with a type-prefixed id
 * (e.g. `CITY-3000001653`), which breaks the `/relax` URL it's used to build.
 * `cityID` is always the bare numeric city id, so prefer it over `id` here.
 * Scoped to `CITY` only — `HOTEL`/`AIRPORT`/`POI`/`NEIGHBOURHOOD` etc. need their
 * own entity id (`preferredhotelids`, path segment, `clusters`), not the city id.
 */
function normalizeAiSearchItem(item: LOCATION_SEARCH_TYPE): LOCATION_SEARCH_TYPE {
  if (item.type !== 'CITY' || !item.cityID) return item
  return { ...item, id: item.cityID }
}

/**
 * Fetches AI search suggestions from `hotels/aisearch` for Describe Your Trip.
 */
export async function fetchAiQuerySuggestions({
  query,
  signal,
}: FetchAiQueryParams): Promise<LOCATION_SEARCH_TYPE[]> {
  try {
    const response = await fetch(buildAiSearchUrl(query), signal ? { signal } : {})
    const { searchItems } = (await response.json()) as {
      searchItems: LOCATION_SEARCH_TYPE[] | null
    }

    return (searchItems ?? []).map(normalizeAiSearchItem)
  } catch (error) {
    if (!isAbortError(error)) logAiSearchRequestFailed(query)
    throw error
  }
}

/**
 * Resolves a free-text Describe Your Trip query via `hotels/aisearch`.
 * Returns the first match — typically an `AI_QUERY` location for `/relax` URLs.
 */
export async function resolveAiQuery({
  query,
  signal,
}: FetchAiQueryParams): Promise<LOCATION_SEARCH_TYPE | null> {
  const searchItems = await fetchAiQuerySuggestions({ query, signal })
  return searchItems[0] ?? null
}
